import { supabase } from "@/integrations/supabase/client";

export interface NetworkAdaptationPrompt {
  id: string;
  network: string;
  title_prompt: string | null;
  description_prompt: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NetworkAdaptation {
  id: string;
  video_content_id: string;
  network: string;
  adapted_title: string | null;
  adapted_description: string | null;
  is_user_modified: boolean;
  generated_at: string | null;
  created_at: string;
  updated_at: string;
}

// ============ PROMPTS (Admin) ============

export async function getNetworkPrompts(): Promise<NetworkAdaptationPrompt[]> {
  const { data, error } = await supabase
    .from("network_adaptation_prompts")
    .select("*")
    .order("network");

  if (error) {
    console.error("[networkAdaptations] Error fetching prompts:", error);
    throw error;
  }

  return data || [];
}

export async function updateNetworkPrompt(
  id: string, 
  updates: Partial<Pick<NetworkAdaptationPrompt, "title_prompt" | "description_prompt" | "is_active">>
): Promise<void> {
  const { error } = await supabase
    .from("network_adaptation_prompts")
    .update(updates)
    .eq("id", id);

  if (error) {
    console.error("[networkAdaptations] Error updating prompt:", error);
    throw error;
  }
}

// ============ ADAPTATIONS (User) ============

export async function getAdaptationsForContent(videoContentId: string): Promise<NetworkAdaptation[]> {
  const { data, error } = await supabase
    .from("video_content_network_adaptations")
    .select("*")
    .eq("video_content_id", videoContentId);

  if (error) {
    console.error("[networkAdaptations] Error fetching adaptations:", error);
    throw error;
  }

  return data || [];
}

export async function getAdaptationForNetwork(
  videoContentId: string, 
  network: string
): Promise<NetworkAdaptation | null> {
  const { data, error } = await supabase
    .from("video_content_network_adaptations")
    .select("*")
    .eq("video_content_id", videoContentId)
    .eq("network", network)
    .maybeSingle();

  if (error) {
    console.error("[networkAdaptations] Error fetching adaptation:", error);
    throw error;
  }

  return data;
}

export async function updateAdaptation(
  videoContentId: string,
  network: string,
  updates: { adapted_title?: string; adapted_description?: string }
): Promise<void> {
  const { error } = await supabase
    .from("video_content_network_adaptations")
    .upsert({
      video_content_id: videoContentId,
      network,
      adapted_title: updates.adapted_title,
      adapted_description: updates.adapted_description,
      is_user_modified: true
    }, {
      onConflict: "video_content_id,network"
    });

  if (error) {
    console.error("[networkAdaptations] Error updating adaptation:", error);
    throw error;
  }
}

export async function generateAdaptation(
  videoContentId: string,
  network: string,
  originalTitle: string,
  originalDescription: string
): Promise<{ adapted_title?: string; adapted_description?: string }> {
  console.log(`[networkAdaptations] Generating adaptation for ${network}`);

  const { data, error } = await supabase.functions.invoke("adapt-content-for-network", {
    body: {
      video_content_id: videoContentId,
      network,
      original_title: originalTitle,
      original_description: originalDescription
    }
  });

  if (error) {
    console.error("[networkAdaptations] Edge function error:", error);
    throw error;
  }

  if (!data?.success) {
    throw new Error(data?.error || "Failed to generate adaptation");
  }

  return {
    adapted_title: data.adapted_title,
    adapted_description: data.adapted_description
  };
}

export async function generateAllAdaptations(
  videoContentId: string,
  networks: string[],
  originalTitle: string,
  originalDescription: string
): Promise<Map<string, { adapted_title?: string; adapted_description?: string }>> {
  const results = new Map<string, { adapted_title?: string; adapted_description?: string }>();

  // Generate adaptations in parallel
  const promises = networks.map(async (network) => {
    try {
      const adaptation = await generateAdaptation(
        videoContentId,
        network,
        originalTitle,
        originalDescription
      );
      results.set(network, adaptation);
    } catch (error) {
      console.error(`[networkAdaptations] Failed to adapt for ${network}:`, error);
    }
  });

  await Promise.all(promises);
  return results;
}

// Network display info
export const NETWORK_INFO: Record<string, { label: string; maxDescription: number; maxTitle?: number; hasTitle: boolean }> = {
  instagram: { label: "Instagram", maxDescription: 2200, hasTitle: false },
  tiktok: { label: "TikTok", maxDescription: 2200, hasTitle: false },
  facebook: { label: "Facebook", maxDescription: 2200, hasTitle: false },
  linkedin: { label: "LinkedIn", maxDescription: 3000, hasTitle: false },
  threads: { label: "Threads", maxDescription: 500, hasTitle: false },
  youtube: { label: "YouTube", maxDescription: 5000, maxTitle: 100, hasTitle: true }
};
